import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectAnalyticsOfferStatus, selectAnalyticsProduct } from '../../../../../../../selectors/analytics';
import {
  selectRecommendedPackages,
  selectSelectedRecommendedPackageId,
  selectIsRecommendedPackagesAvailable,
  selectDefaultRecommendedPackagePublicationPrice,
} from '../../../../../../../selectors/recommendedPackages';
import { selectIsRecommendedPackagePaymentStatusCheckInProgress } from '../../../../../../../selectors/depositAccount';
import { setSelectedRecommendedPackageId } from '../../../../../../../actions/recommendedPackages';
import { selectBillingUserType } from '../../../../../../../selectors/billingInfo';
import { selectConfigValue } from '../../../../../../../selectors/config';
import { IRecommendedPackage } from '../../../../../../../types/reducers/recommendedPackages';
import { EPaymentInitSource } from '../../../../../../../types/microfrontends/buyPackage';
import { useIsMobileDevice, useDeviceCondition } from '../../../../../../../hooks';
import { useBuyPackage } from '../../../../../../../hooks/useBuyPackage';
import {
  trackDefaultPublicationClick,
  trackRecommendedPackageClick,
  trackShowRecommendedPackagesWidget,
} from './tracking';
import { mapPackageToModalData, mapPackageTrackingData } from './utils';

export const useRecommendedPackages = () => {
  const dispatch = useDispatch();
  const isMobile = useIsMobileDevice();
  const { buyPackage } = useBuyPackage();

  const analyticsOfferStatus = useSelector(selectAnalyticsOfferStatus);
  const { id: form_id, variant: vas } = useSelector(selectAnalyticsProduct);
  const billingUserType = useSelector(selectBillingUserType);
  const siteType = useDeviceCondition('mobile', 'desktop');

  const isPaymentStatusCheckInProgress = useSelector(selectIsRecommendedPackagePaymentStatusCheckInProgress);
  const isRecommendedPackagesAvailable = useSelector(selectIsRecommendedPackagesAvailable);
  const enableRecommendedPackagesMobileView = useSelector(selectConfigValue('enableRecommendedPackagesMobileView'));
  const showForMobile = enableRecommendedPackagesMobileView || !isMobile;
  const isRender = showForMobile && !isPaymentStatusCheckInProgress && isRecommendedPackagesAvailable;

  const recommendedPackages = useSelector(selectRecommendedPackages);
  const selectedRecommendedPackageId = useSelector(selectSelectedRecommendedPackageId);

  const offerPrice = useSelector(selectDefaultRecommendedPackagePublicationPrice);

  const resetToDefaultPackage = useCallback(() => {
    // Обнуляем выбранный пакет в дефолтную публикацию
    dispatch(setSelectedRecommendedPackageId(null));
  }, [dispatch]);

  const handleDefaultPublicationClick = useCallback(() => {
    resetToDefaultPackage();

    trackDefaultPublicationClick({
      analyticsOfferStatus,
      siteType,
      form_id,
      vas,
    });
  }, [dispatch, resetToDefaultPackage, analyticsOfferStatus, form_id, vas, siteType]);

  const handleRecommendedPackageClick = useCallback(
    (recommendedPackage: IRecommendedPackage) => {
      dispatch(setSelectedRecommendedPackageId(recommendedPackage.packageId));

      // Открываем модалку из микрофронта pay-package-frontend
      buyPackage({
        packageData: mapPackageToModalData(recommendedPackage),
        paymentInitSource: EPaymentInitSource.AddFormDeposit,
        billingUserType: billingUserType!,
      });

      trackRecommendedPackageClick({
        packageInfo: mapPackageTrackingData(recommendedPackage),
        analyticsOfferStatus,
        siteType,
        form_id,
        vas,
      });
    },
    [dispatch, buyPackage, analyticsOfferStatus, form_id, vas, billingUserType, siteType],
  );

  useEffect(() => {
    if (isRender) {
      trackShowRecommendedPackagesWidget({
        packagesInfo: recommendedPackages.map(recommendedPackage => mapPackageTrackingData(recommendedPackage)),
        analyticsOfferStatus,
        siteType,
        form_id,
        vas,
      });
    }
  }, [isRender, recommendedPackages]);

  useEffect(() => {
    window.addEventListener('payment-cancel', resetToDefaultPackage);

    return () => {
      window.removeEventListener('payment-cancel', resetToDefaultPackage);
    };
  }, []);

  return {
    isRender,
    isMobile,
    offerPrice,
    recommendedPackages,
    selectedRecommendedPackageId,
    handleDefaultPublicationClick,
    handleRecommendedPackageClick,
  };
};
